import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Empty,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { createFaq, deleteFaq, listAdminFaqs, updateFaq } from "./faqApi";
import { getApiErrorMessage } from "../../utils/apiError";
import FaqAnswerEditor from "./FaqAnswerEditor";
import { faqAnswerHasText, plainTextToFaqHtml } from "./sanitizeFaqHtml";
import { useFaqPanelSize } from "./faqLayout";
import "./faq.css";

const emptyForm = () => ({
  question: "",
  answer: "",
  isActive: true,
});

const FaqAdminModal = ({ open, onClose, token }) => {
  const { modalWidth } = useFaqPanelSize();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [togglingId, setTogglingId] = useState(null);

  const loadItems = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    try {
      const data = await listAdminFaqs(token);
      setItems(data);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load FAQs."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      setMode("list");
      setEditingId(null);
      setForm(emptyForm());
      loadItems();
    }
  }, [open, loadItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setMode("form");
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      question: item.question || "",
      answer: plainTextToFaqHtml(item.answer || ""),
      isActive: item.isActive !== false,
    });
    setMode("form");
  };

  const handleSave = async () => {
    const question = form.question.trim();
    if (!question) {
      message.warning("Add a question.");
      return;
    }
    if (!faqAnswerHasText(form.answer)) {
      message.warning("Add an answer.");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateFaq(token, editingId, form);
        message.success("FAQ updated.");
      } else {
        await createFaq(token, form);
        message.success("FAQ created.");
      }
      setMode("list");
      await loadItems();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to save FAQ."));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item, isActive) => {
    setTogglingId(item.id);
    try {
      await updateFaq(token, item.id, { isActive });
      setItems((prev) =>
        prev.map((faq) => (faq.id === item.id ? { ...faq, isActive } : faq))
      );
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to update FAQ."));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFaq(token, id);
      message.success("FAQ deleted.");
      await loadItems();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to delete FAQ."));
    }
  };

  return (
    <Modal
      title={mode === "form" ? (editingId ? "Edit FAQ" : "New FAQ") : "FAQ"}
      open={open}
      onCancel={mode === "form" ? () => setMode("list") : onClose}
      width={modalWidth}
      className="faq-admin-modal"
      rootClassName="faq-admin-modal-root"
      destroyOnHidden
      footer={
        mode === "form" ? (
          <Space>
            <Button onClick={() => setMode("list")}>Back</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {mode === "list" ? (
        <>
          <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary">
              Questions users can open from the header FAQ icon.
            </Typography.Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add FAQ
            </Button>
          </Flex>
          {loading ? (
            <div className="faq-drawer-loading">
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <Empty description="No FAQs yet" />
          ) : (
            items.map((item) => (
              <div key={item.id} className="faq-admin-item">
                <div className="faq-admin-item-copy">
                  <Typography.Text strong>{item.question}</Typography.Text>
                  <div>
                    <Typography.Text type="secondary">
                      {item.isActive ? "Active · visible to users" : "Inactive · hidden from users"}
                    </Typography.Text>
                  </div>
                </div>
                <Space>
                  <Switch
                    checked={item.isActive !== false}
                    loading={togglingId === item.id}
                    onChange={(checked) => handleToggleActive(item, checked)}
                  />
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(item)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this FAQ?"
                    okText="Delete"
                    onConfirm={() => handleDelete(item.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              </div>
            ))
          )}
        </>
      ) : (
        <>
          <Typography.Text strong>Question</Typography.Text>
          <Input
            value={form.question}
            maxLength={240}
            placeholder="How do I upload an invoice?"
            style={{ margin: "8px 0 16px" }}
            onChange={(event) => setForm((prev) => ({ ...prev, question: event.target.value }))}
          />
          <Typography.Text strong>Answer</Typography.Text>
          <div style={{ margin: "8px 0 16px" }}>
            <FaqAnswerEditor
              value={form.answer}
              onChange={(answer) => setForm((prev) => ({ ...prev, answer }))}
            />
          </div>
          <Flex align="center" justify="space-between">
            <Typography.Text strong>Visible to users</Typography.Text>
            <Switch
              checked={form.isActive}
              onChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
            />
          </Flex>
        </>
      )}
    </Modal>
  );
};

export default FaqAdminModal;
